import { useCallback, useEffect, useRef, useState } from 'react';
import { Box } from '@cad-arc';
import {
    ESRI_HIGHLIGHT_FILL_OPACITY,
    ESRI_MAP_DEFAULT_ZOOM_LEVEL,
    MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
} from '../configurations';
import { Point, WebMap, Color } from '../constructors';
import { storageHelpers } from '../../common/helpers/browser-storage';
import { isValidLatLng } from '../../location/helpers';
import type { GeoPoint } from '../../location/types';
import type { EsriMapInterface, WebGLUnavailableReason, MapSurface } from '../types';
import { MAP_SURFACE_ATTRIBUTE } from '../types';
import { getWebGLUnavailableReason } from '../helpers/webgl-unavailable-reason';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { useEsriIdentityContext } from '../esri-identity-context';
import { createInitialWebMap, shouldLoadAlternativeBasemap } from './helpers';
import { useOwnedMapView } from './use-owned-map-view';
import { useEsriMapStore } from '~/map-window/hooks/use-esri-map-store';
import { colors } from '~/colors/colors';

type UseEsriEmbeddedESModuleMap = {
    center: GeoPoint;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
    surface: MapSurface;
};
const useEsriEmbeddedESModuleMap = ({
    center,
    mapRef,
    surface,
}: UseEsriEmbeddedESModuleMap): EsriMapInterface => {
    const { mapMode: storeMapMode } = useEsriMapStore();
    const { getEsriToken, isEsriIdentityResolved } = useEsriIdentityContext();
    // Waits for the initial token fetch to settle before constructing a Basemap, so the first
    // attempt isn't a guaranteed 401 while the fetch is still in flight. Latches true
    // permanently, same as the main map window's equivalent gate, so a later flag toggle can't
    // re-block an already-mounted map.
    const [hasEsriIdentityResolvedOnce, setHasEsriIdentityResolvedOnce] =
        useState(isEsriIdentityResolved);
    if (isEsriIdentityResolved && !hasEsriIdentityResolvedOnce) {
        setHasEsriIdentityResolvedOnce(true);
    }
    const enabledFeatures = useEnabledFeatures();

    // Construction, teardown and health monitoring of the MapView all belong to
    // `useOwnedMapView` — it owns the `mapView` state so its teardown effect cannot be
    // defeated by this hook's dependency array.
    const { mapView, createMapView, addDisposer } = useOwnedMapView({
        surface,
        mapRef,
        releaseWebGLContextOnTeardown:
            enabledFeatures.mapReleaseWebglContextOnTeardownEnabled === true,
    });

    const [webMap, setWebMap] = useState<WebMap>();
    const [mapMode, setMapMode] = useState<string>('');
    const [webGLUnavailable, setWebGLUnavailable] = useState<WebGLUnavailableReason>();
    const [isMapLoading, setIsMapLoading] = useState<boolean>(true);
    const [isMapLoadingFailed, setIsMapLoadingFailed] = useState<boolean>(false);

    const setMapLoadFailed = useCallback((failed: boolean) => {
        if (failed) {
            storageHelpers.setItem(
                MAP_ALTERNATIVE_BASEMAP_ACTIVATION_DATE_KEY,
                new Date().toISOString()
            );
        }
        setIsMapLoadingFailed(failed);
    }, []);

    useEffect(
        function initializeMap() {
            if (
                !hasEsriIdentityResolvedOnce ||
                (mapMode === storeMapMode &&
                    (!!mapView || !isValidLatLng(center.latitude, center.longitude)))
            ) {
                return;
            }

            const isAlternativeBasemap = shouldLoadAlternativeBasemap();

            const { webmap, cancelPendingLoadEffects } = createInitialWebMap({
                isAlternativeBasemap,
                isEmbeddedBasemap: true,
                mapMode: storeMapMode,
                getEsriToken,
            });

            const view = createMapView({
                container: mapRef.current ?? undefined,
                map: webmap,
                center: center as Point,
                zoom: ESRI_MAP_DEFAULT_ZOOM_LEVEL,
                constraints: {
                    rotationEnabled: false,
                },
                ui: undefined,
                highlightOptions: {
                    color: new Color(colors.esriColors.highlightColor),
                    fillOpacity: ESRI_HIGHLIGHT_FILL_OPACITY,
                    haloOpacity: 1,
                },
            });
            addDisposer(view, cancelPendingLoadEffects);

            view.when().catch((error) => {
                // Logged by `useOwnedMapView`; this hook only owns its own load state.
                const reason = getWebGLUnavailableReason(error);
                setWebGLUnavailable(reason);
                if (reason === 'stopped-working') {
                    // A GPU problem, not a basemap one: don't move this machine to the
                    // alternative basemap for the next 16 hours.
                    setIsMapLoadingFailed(true);
                } else {
                    setMapLoadFailed(true);
                }
            });

            view.watch('ready', () => {
                setIsMapLoading(false);
            });

            setWebMap(webmap);
            setMapMode(storeMapMode);
        },
        // `mapView` stays a dependency so a map-mode change recreates the view — the
        // resulting re-run early-returns above. Teardown is NOT registered here: it
        // lives in `useOwnedMapView`, keyed on the view, because a cleanup returned
        // from this effect would fire immediately after mount (when `mapView` is set)
        // and never again.
        [
            mapView,
            createMapView,
            addDisposer,
            setMapLoadFailed,
            center,
            mapRef,
            storeMapMode,
            mapMode,
            getEsriToken,
            hasEsriIdentityResolvedOnce,
        ]
    );

    return { mapView, webMap, webGLUnavailable, isMapLoading, isMapLoadingFailed };
};

type UseEsriEmbeddedBaseMapProps = { center: GeoPoint; surface: MapSurface };

export const useEsriEmbeddedBaseMap = ({
    center,
    surface,
}: UseEsriEmbeddedBaseMapProps): EsriMapInterface & {
    mapComponent: React.ReactElement;
    mapRef: React.MutableRefObject<HTMLDivElement | null>;
} => {
    const mapRef = useRef<HTMLDivElement | null>(null);
    // The surface attribute is how a leaked WebGL context is traced back to this map:
    // the registry reads the nearest `data-map-surface` ancestor of each canvas.
    const mapComponent = (
        <Box w="100%" h="100%" ref={mapRef} {...{ [MAP_SURFACE_ATTRIBUTE]: surface }} />
    );
    const { mapView, webMap, webGLUnavailable, isMapLoading, isMapLoadingFailed } =
        useEsriEmbeddedESModuleMap({ center, mapRef, surface });

    return {
        mapComponent,
        mapView,
        webMap,
        mapRef,
        webGLUnavailable,
        isMapLoading,
        isMapLoadingFailed,
    };
};
